import { useEffect, useState } from 'react';
import io from 'socket.io-client';
import { navigate } from 'gatsby';
import { removeItemFromLocalStorage } from 'common/utils/local-storage/local-storage';
import { useIfDefined } from 'common/hooks';
import { socketMessages } from '../../utils/socket';
import logger from '../../utils/logger';

const useOatSocket = ({
  clientId,
  removeAllItems,
  resetOatState,
  setIsConnectTimeout,
  updateOatState,
  restaurantId,
  setIsLoading,
  setSocket,
  currentState,
  setIsRedundant,
  paymentMode = false,
  features,
}) => {
  const [socket, setNewSocket] = useState<any>();
  const [socketError, setSocketError] = useState(null);

  if (socketError) {
    throw new Error(socketError);
  }

  useIfDefined(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const targetState = urlSearchParams.get('targetState');

    const newSocket = io(window.AZZURRI_ENV.apiEndpoint, {
      query: {
        clientId,
        currentState: targetState,
        paymentMode,
      },
    });

    setNewSocket(newSocket);
    removeItemFromLocalStorage('waiterId');
    logger.info({ msg: 'create socket with client id', clientId });

    newSocket.on(socketMessages.clientUnknown, (payload) => {
      logger.info({ msg: 'Client unknown event', ...payload });
      removeAllItems();
      resetOatState();
      navigate('/', { replace: true });
    });

    newSocket.on(socketMessages.disconnect, () => {
      logger.info({ msg: 'Disconnected' });
      navigate('/', { replace: true });
    });

    newSocket.on(socketMessages.redundantConnection, () => {
      setIsRedundant(true);
    });

    newSocket.on('reconnect_attempt', (reconnectErrorCount) => {
      if (reconnectErrorCount > 0) {
        setIsConnectTimeout(true);
      }
    });

    newSocket.on('reconnect', () => {
      setIsConnectTimeout(false);
    });

    const emit = (message, payload) => {
      newSocket.emit(message, {
        clientId,
        restaurantId,
        ...payload,
      });
      logger.info({
        msg: 'Emit message',
        message,
        clientId,
        payload,
      });
    };

    const changeState = (message, payload) => {
      emit(message, payload);
      setIsLoading(true);
    };

    setSocket({
      emit,
      changeState,
    });

    return () => {
      // Closing on replace/unmount isn't a lost connection, so skip the disconnect redirect.
      newSocket.off(socketMessages.disconnect);
      newSocket.disconnect();
    };
  }, clientId);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        socket.disconnect();
      } else {
        socket.connect();
      }
    };

    if (!paymentMode && features?.disconnectUserFixEnabled) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [socket, paymentMode, features?.disconnectUserFixEnabled]);

  useEffect(() => {
    const listener = (payload) => {
      logger.info({
        msg: 'Receive current state',
        payload,
      });
      const { currentState: remoteCurrentState, ...remoteData } = payload;
      updateOatState({ currentState: remoteCurrentState, data: remoteData });
    };

    if (socket) {
      socket.on(socketMessages.currentState, listener);
      socket.on(socketMessages.appError, (err) => {
        setSocketError(err);
      });
    }
    return () => {
      if (socket) {
        socket.off(socketMessages.currentState, listener);
        socket.off(socketMessages.appError);
      }
    };
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, [socket, currentState]);
};

export default useOatSocket;
